/**
 * Notice — 인라인 안내 띠 (배너·폼 위 경고·상태 안내)
 *
 * <Notice variant="warning" title="연결을 확인해주세요" action={{ label: '다시 확인', onClick }}>
 *   광고 차단 앱이 왓퀴즈 연결을 막았을 수 있어요.
 * </Notice>
 *
 * - variant : neutral | info | warning | error — 색은 아이콘과 왼쪽 막대에만 쓴다. 표면은 항상 밝은 회색.
 * - action  : { label, onClick, disabled, 'aria-expanded', 'aria-controls' } — 한 번에 하나만 보인다.
 * - role    : 기본 status(polite). error는 alert(assertive). 필요하면 덮어쓴다.
 * - children: 본문. title이 있으면 제목 아래 보조 문장으로 놓인다.
 * - footer  : 펼침 도움말 등 본문 아래 추가 영역.
 */

import React from 'react';
import './Notice.css';

export const NOTICE_VARIANTS = ['neutral', 'info', 'warning', 'error'];

const joinClassNames = (...values) => values.filter(Boolean).join(' ');

const ICONS = {
  neutral: (
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <circle cx="10" cy="10" r="8.25" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="10" cy="6.4" r="1.05" fill="currentColor" />
      <path d="M10 9.2v5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  ),
  info: (
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <circle cx="10" cy="10" r="9" fill="currentColor" />
      <circle cx="10" cy="6.3" r="1.15" fill="#fff" />
      <path d="M10 9.2v5.2" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <path
        d="M9.13 2.9a1 1 0 0 1 1.74 0l7.1 12.6a1 1 0 0 1-.87 1.5H2.9a1 1 0 0 1-.87-1.5z"
        fill="currentColor"
      />
      <path d="M10 7.2v4.3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="10" cy="14.1" r="1.05" fill="#fff" />
    </svg>
  ),
  error: (
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <circle cx="10" cy="10" r="9" fill="currentColor" />
      <path d="M7 7l6 6M13 7l-6 6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
};

const isAction = (action) => Boolean(action && typeof action === 'object' && action.label);

const Notice = ({
  action = null,
  'aria-live': ariaLive,
  children,
  className,
  compact = false,
  footer = null,
  icon,
  id,
  role,
  testId = 'notice',
  title = null,
  variant = 'neutral',
}) => {
  const resolvedVariant = NOTICE_VARIANTS.includes(variant) ? variant : 'neutral';
  const resolvedRole = role || (resolvedVariant === 'error' ? 'alert' : 'status');
  const resolvedLive = ariaLive || (resolvedRole === 'alert' ? 'assertive' : 'polite');
  const hasTitle = title !== null && title !== undefined && title !== '';
  const hasBody = children !== null && children !== undefined && children !== false && children !== '';
  const showIcon = icon !== null;

  return (
    <section
      aria-live={resolvedLive}
      className={joinClassNames(
        'whatquiz-notice',
        `whatquiz-notice--${resolvedVariant}`,
        compact && 'whatquiz-notice--compact',
        className,
      )}
      data-testid={testId}
      id={id}
      role={resolvedRole}
    >
      <div className="whatquiz-notice__main">
        {showIcon && (
          <span className="whatquiz-notice__icon" data-testid={`${testId}-icon`}>
            {icon === undefined ? ICONS[resolvedVariant] : icon}
          </span>
        )}
        <div className="whatquiz-notice__copy">
          {hasTitle && <strong className="whatquiz-notice__title">{title}</strong>}
          {hasBody && (
            <div className="whatquiz-notice__text">{children}</div>
          )}
        </div>
        {isAction(action) && (
          <div className="whatquiz-notice__action">
            <button
              aria-controls={action['aria-controls']}
              aria-expanded={action['aria-expanded']}
              className="whatquiz-notice__button"
              data-testid={`${testId}-action`}
              disabled={Boolean(action.disabled)}
              onClick={action.onClick}
              type="button"
            >
              {action.label}
            </button>
          </div>
        )}
      </div>
      {footer && (
        <div className="whatquiz-notice__footer" data-testid={`${testId}-footer`}>{footer}</div>
      )}
    </section>
  );
};

export default Notice;
